import { getFormattedDate, getYear } from '../../../helpers/multimediaUtils';

/**
 * Extract plain text from a field that may be a string, a RichText object
 * ({ data, output }), or undefined.
 */
const extractFieldText = (field) => {
  if (!field) return '';
  if (typeof field === 'string') return field;
  const html = field.output || field.data || '';
  return html.replace(/<[^>]*>/g, '').trim();
};

/**
 * Returns a formatted HTML citation string with the publication metadata,
 * including authors, date, title, subtitle (volume name or conference title),
 * and an optional link to the publication.
 *
 * Works with both full content objects (detail view) and metadata-only objects
 * (listing via searchContent).
 *
 *  @param {Object} content - The content item containing publication metadata.
 *  @returns {string} - The formatted citation HTML string.
 */

export const buildCitation = (content) => {
  if (!content) return '';

  const isWritten = content['@type'] === 'written_publication';
  let subtitle = '';
  let optionalText = '';

  if (isWritten) {
    subtitle = extractFieldText(content?.volume_name);
    optionalText = content?.volume_pages ? ', ' + content.volume_pages : '';
  } else {
    subtitle = extractFieldText(content?.conference_title);
    optionalText = content?.conference_location
      ? ', ' + content.conference_location
      : '';
  }

  const publicationDate = content?.publication_date
    ? new Date(content.publication_date)
    : null;
  const date = publicationDate
    ? isWritten
      ? getYear(publicationDate)
      : getFormattedDate(publicationDate)
    : '';

  const authors = content?.publication_authors || '';
  const title = content?.title || '';
  const titlePart = title.endsWith('.') ? title : `${title}.`;
  const subtitlePart = subtitle ? ` ${subtitle}${optionalText}.` : '';

  const url =
    content?.url || (content?.doi ? `https://doi.org/${content.doi}` : '');
  const link = url
    ? ` <a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`
    : '';

  return `${authors}. (${date}). ${titlePart}${subtitlePart}${link}`;
};